import { useCallback, useEffect, useRef, useState } from "react";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

// Debounced autosave. Call `schedule(payload)` on every change; the latest
// payload is persisted after `delay` ms of quiet. Also flushes on unmount.
export function useAutosave<T>(save: (payload: T) => Promise<unknown>, delay = 900) {
  const [status, setStatus] = useState<SaveStatus>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<T | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (pending.current === null) return;
    const payload = pending.current;
    pending.current = null;
    setStatus("saving");
    try {
      await saveRef.current(payload);
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }, []);

  const schedule = useCallback(
    (payload: T) => {
      pending.current = payload;
      setStatus("saving");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, delay);
    },
    [delay, flush],
  );

  useEffect(() => {
    return () => {
      // best-effort flush on unmount
      if (pending.current !== null) {
        const payload = pending.current;
        saveRef.current(payload).catch(() => {});
      }
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  return { status, schedule, flush };
}
